import { authorisedHeaders } from "@/lib/api/client"
import { env } from "@/lib/env"

type Listener = (paths: string[]) => void

type ChangesStream = {
  close: () => void
}

type ServerEvent = {
  event: string
  data: string
}

const defaultRetryMs = 3000

let live = false

function parseEvent(block: string): ServerEvent & { retry?: number } {
  const parsed: ServerEvent & { retry?: number } = {
    event: "message",
    data: "",
  }

  for (const line of block.split("\n")) {
    const separator = line.indexOf(":")
    const field = separator === -1 ? line : line.slice(0, separator)
    const value =
      separator === -1 ? "" : line.slice(separator + 1).replace(/^ /, "")

    if (field === "event") {
      parsed.event = value
    } else if (field === "data") {
      parsed.data = parsed.data ? `${parsed.data}\n${value}` : value
    } else if (field === "retry" && /^\d+$/.test(value)) {
      parsed.retry = Number(value)
    }
  }

  return parsed
}

async function readEvents(
  response: Response,
  onEvent: (event: ServerEvent & { retry?: number }) => void
): Promise<void> {
  const body = response.body
  if (!body) {
    return
  }

  const reader = body.pipeThrough(new TextDecoderStream()).getReader()
  let buffered = ""

  for (;;) {
    const { done, value } = await reader.read()
    if (done) {
      return
    }

    buffered = (buffered + value).replace(/\r\n?/g, "\n")
    let boundary = buffered.indexOf("\n\n")

    while (boundary !== -1) {
      const block = buffered.slice(0, boundary)
      buffered = buffered.slice(boundary + 2)
      if (block.trim() !== "") {
        onEvent(parseEvent(block))
      }
      boundary = buffered.indexOf("\n\n")
    }
  }
}

export function openChanges(
  onChanged: Listener,
  onReconnected: () => void
): ChangesStream {
  const controller = new AbortController()
  let retryMs = defaultRetryMs
  let hasBeenLive = false
  let timer: ReturnType<typeof setTimeout> | undefined

  const connect = async (): Promise<void> => {
    try {
      const response = await fetch(`${env.apiUrl}/changes`, {
        headers: authorisedHeaders(),
        cache: "no-store",
        signal: controller.signal,
      })

      if (response.status === 401 || response.status === 403) {
        live = false
        return
      }

      if (response.ok) {
        if (hasBeenLive) {
          onReconnected()
        }
        hasBeenLive = true
        live = true

        await readEvents(response, (event) => {
          if (event.retry !== undefined) {
            retryMs = event.retry
          }
          if (event.event === "changed") {
            const { paths } = JSON.parse(event.data) as { paths: string[] }
            onChanged(paths)
          }
        })
      }
    } catch {
      if (controller.signal.aborted) {
        return
      }
    }

    live = false
    if (!controller.signal.aborted) {
      timer = setTimeout(() => void connect(), retryMs)
    }
  }

  void connect()

  return {
    close: () => {
      clearTimeout(timer)
      controller.abort()
      live = false
    },
  }
}

export function pollUnlessLive(ms: number): number | false {
  return live ? false : ms
}
